import { useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField, SelectField } from '../components/Field.jsx';
import RowActions from '../components/RowActions.jsx';
import { Loader, ErrorAlert, EmptyRow, StatusBadge, InfoAlert } from '../components/States.jsx';

import useForm from '../hooks/useForm.js';
import {
  useListCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useToggleCustomerMutation,
  useDeleteCustomerMutation,
} from '../store/apiSlice.js';
import { customerSchema, CUSTOMER_TYPES } from '../validation/schemas.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import { fmtDate, dash } from '../utils/format.js';
import { downloadCsv } from '../utils/csv.js';

const BLANK = { company: '', contact: '', address: '', gst: '', phone: '', customerType: 'Standard' };

export default function CustomersPage() {
  const showToast = useToast();
  const { reload: reloadReferenceData } = useReferenceData();
  const { data: customers, isLoading: loading, error: apiError, refetch: reload } = useListCustomersQuery();
  const error = apiError?.message ?? null;

  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [toggleCustomer] = useToggleCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();

  const form = useForm(BLANK, customerSchema);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const cancelEdit = () => {
    setEditingId(null);
    form.reset(BLANK);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.validate()) return;

    setBusy(true);
    try {
      if (editingId) await updateCustomer({ id: editingId, payload: form.values }).unwrap();
      else await createCustomer(form.values).unwrap();
      showToast(editingId ? 'Customer updated' : 'Customer added');
      cancelEdit();
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      form.applyServerErrors(err.details);
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (customer) => {
    setEditingId(customer.id);
    form.fill({
      company: customer.company,
      contact: customer.contact,
      address: customer.address,
      gst: customer.gst,
      phone: customer.phone,
      customerType: customer.customerType || 'Standard',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete "${customer.company}"?`)) return;
    setBusy(true);
    try {
      await deleteCustomer(customer.id).unwrap();
      if (editingId === customer.id) cancelEdit();
      showToast('Customer deleted');
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (customer) => {
    setBusy(true);
    try {
      const updated = await toggleCustomer(customer.id).unwrap();
      showToast(updated.status === 'disabled' ? 'Customer disabled' : 'Customer enabled');
      await Promise.all([reload(), reloadReferenceData()]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    downloadCsv('customers', [
      { label: 'Sr. No', value: (customer) => customers.indexOf(customer) + 1 },
      { label: 'Customer', value: (customer) => customer.company },
      { label: 'Type', value: (customer) => customer.customerType || 'Standard' },
      { label: 'Contact', value: (customer) => customer.contact },
      { label: 'Phone', value: (customer) => customer.phone },
      { label: 'GST', value: (customer) => customer.gst },
      { label: 'Address', value: (customer) => customer.address },
      { label: 'Status', value: (customer) => customer.status || 'active' },
      { label: 'Created At', value: (customer) => customer.createdAt || '' },
      { label: 'Last Updated', value: (customer) => customer.updatedAt || '' },
    ], customers || []);
  };

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Customer Management"
        subtitle="Regular customers can have their own prices. Standard customers always use the Common price list."
      />

      <InfoAlert>
        <strong>Standard</strong> customers are billed from the Common price list. <strong>Regular</strong> customers
        use their own price where one exists in Price Management, and fall back to Common otherwise.
      </InfoAlert>

      <Card title={editingId ? 'Edit Customer' : 'Add Customer'} tag="Customers">
        {editingId && (
          <div className="editing-bar">
            <span>Editing “{form.values.company}”</span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}
        <form onSubmit={submit} noValidate>
          <div className="form-grid">
            <TextField label="Customer Name" placeholder="e.g. XYZ Salon" maxLength={80} {...form.fieldProps('company')} />
            <TextField label="Contact Person" placeholder="Full name" maxLength={60} {...form.fieldProps('contact')} />
            <TextField label="Phone Number" placeholder="+91 99999 12345" {...form.fieldProps('phone')} />
            <TextField label="GST Number" placeholder="08AAECX9012C1Z9" maxLength={15} hint="15 characters. Leave blank if unregistered." {...form.fieldProps('gst')} />
            <SelectField label="Customer Type" {...form.fieldProps('customerType')}>
              {CUSTOMER_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type} Customer
                </option>
              ))}
            </SelectField>
            <TextField label="Address" full placeholder="Street, city" maxLength={200} {...form.fieldProps('address')} />
          </div>
          <div className="btn-row">
            <button className="btn primary" type="submit" disabled={busy}>
              {editingId ? 'Update Customer' : 'Add Customer'}
            </button>
          </div>
        </form>
      </Card>

      <Card title="Customer List" tag="All Customers">
        <div className="table-toolbar">
          <button type="button" className="btn ghost" onClick={exportCsv} disabled={!customers?.length}>
            Export CSV
          </button>
        </div>
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="list-table">
              <thead>
                <tr>
                  <th className="sr-col">Sr. No</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>GST</th>
                  <th>Last Updated</th>
                  <th className="action-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {!customers?.length ? (
                  <EmptyRow colSpan={8}>Add your first customer above</EmptyRow>
                ) : (
                  customers.map((customer, index) => (
                    <tr key={customer.id} className={customer.status === 'disabled' ? 'row-disabled' : ''}>
                      <td className="sr-col">{index + 1}</td>
                      <td>
                        {customer.company} <StatusBadge status={customer.status} />
                      </td>
                      <td>
                        <span className={`badge${customer.customerType === 'Regular' ? ' gold' : ''}`}>
                          {customer.customerType || 'Standard'}
                        </span>
                      </td>
                      <td>{dash(customer.contact)}</td>
                      <td className="nowrap">{dash(customer.phone)}</td>
                      <td className="nowrap">{dash(customer.gst)}</td>
                      <td className="nowrap">{fmtDate(customer.updatedAt)}</td>
                      <td className="action-col">
                        <RowActions
                          status={customer.status}
                          busy={busy}
                          onEdit={() => startEdit(customer)}
                          onDelete={() => handleDelete(customer)}
                          onToggle={() => handleToggle(customer)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
